/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { useState, useEffect } from 'react';
import { Button, FormGroup, Input, Label } from 'reactstrap';
import { getFileData } from '../../utils/image';
import { FileDropZone } from '../files/FileDropZone';
import { FileListContainer } from '../files/FileListContainer';
import G17Client, { formDataConfig } from '../../services/G17Client';
import { optionPortfolioType } from '../../utils/portfolio';
import { getAssurerList } from '../../actions/assurance';
import { SubmitButton } from '@g17eco/molecules/button/SubmitButton';
import { getAnalytics } from '../../services/analytics/AnalyticsService';
import { AnalyticsEvents } from '../../services/analytics/AnalyticsEvents';
import { EvidenceFile, FileWithPreview } from '@g17eco/types/file';
import { AssuranceModalProps } from './assurance-modal';
import { generateUrl } from '../../routes/util';
import { ROUTES } from '../../constants/routes';
import { useHistory } from 'react-router-dom';
import './assurance-modal.scss'
import UserSearch from '../search/UserSearch';
import {
  AssurancePortfolioCreate,
  AssurancePortfolioUpdate
} from '../../types/assurance';
import { Option, SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';

type SetProviderFn = (value: string | undefined, suggestion?: { _id: string, name: string }) => void;

interface AssuranceFormData extends Partial<AssurancePortfolioCreate> {
  assurancePrimaryContactName?: string;
  portfolioId?: string;
  [k: string]: string | undefined;
}

const fileTypes: { [key: string]: string } = {
  'basis_of_reporting': 'Basis of Reporting',
  'management_statement': 'Management Statement',
};

const fileTypeOptions = Object.entries(fileTypes).map(([value, label]) => ({ label, value }));

const RenderAttachment = ({
  label,
  files,
  handleFileAdd,
  handleFileRemove,
  disabled,
  metadata,
}: {
  label: string;
  files: { [key: string]: FileWithPreview | undefined };
  handleFileAdd: (id: string, f: FileWithPreview[]) => void;
  handleFileRemove: (id: string) => void;
  disabled: boolean;
  metadata: {
    initiativeId: string;
  };
}) => {
  const [fileType, setFileType] = useState('');

  const name = fileType ? fileTypes[fileType] : 'Document';

  const fileZoneBody = <span className={'filezone-text'}>
    <i className='fa fa-file mr-3' />Attach {name} by dragging and dropping or <u>selecting</u>
  </span>;

const disabledClass = !fileType || disabled ? 'disabled-zone' : '';

  const filesArray = Object.entries(fileTypes).reduce((a, [id, title]) => {
    const file = files[id];
    if (file !== undefined) {
      a.push({ fileId: id, type: 'file', file: file, title, description: '' });
    }
    return a;
  }, [] as (EvidenceFile & { fileId: string })[]);

  return <div>
    <FormGroup>
      <SelectFactory
       selectType={SelectTypes.LabelSelect}
       labelProps={{ title: label }}
       placeholder='PLEASE SELECT'
       options={fileTypeOptions}
       onChange={(op) => setFileType(op?.value ?? '')}
       isSearchable={false}
       value={fileTypeOptions.find((op) => op.value === fileType)}
       required
       />
    </FormGroup >

    <FileDropZone
      disabled={!fileType || disabled}
      className={`assurance-dropzone ${disabledClass}`}
      multiple={false}
      onDrop={(f: FileWithPreview[]) => handleFileAdd(fileType, f)}>
      {fileZoneBody}
    </FileDropZone>

    <FileListContainer
        metadata={metadata}
        files={filesArray}
        handleFileRemoved={(index) => {
          const f = filesArray[index];
          if (f.fileId) {
            handleFileRemove(f.fileId);
          }
        }}
      />
  </div >
}

interface Assurer {
  _id: string;
  name: string;
}

interface AssuranceModalState {
  loading: boolean;
  loaded: boolean;
  error: boolean;
  errorMessage: undefined | string;
  options: Option[];
}

const requiredFields: (keyof AssuranceFormData)[] = ['portfolioType', 'organizationId', 'assurancePrimaryContactId'];

interface Props extends Omit<AssuranceModalProps, 'isOpen'> {
  initialForm?: AssuranceFormData,
  porfolioId?: string;
}

const AssuranceModalCreate = (props: Props) => {
  const { toggle, initiativeId, surveyId, porfolioId, initialForm } = props;

  const history = useHistory();
  const initialFormData: AssuranceFormData = initialForm ?? {
    portfolioType: optionPortfolioType[0].value,
    organizationId: undefined,
    assurancePrimaryContactName: undefined,
    assurancePrimaryContactId: undefined,
    description: undefined,
    porfolioId,
  };

  const initialSubmitData = { error: false, submitting: false, errorMessage: undefined };
  const initialFiles: { [key: string]: FileWithPreview | undefined } = {};

  const initialAssurers: AssuranceModalState = {
    loading: false,
    loaded: false,
    error: false,
    errorMessage: undefined,
    options: [],
  };

  const [assurers, setAssurers] = useState(initialAssurers);
  const [files, setFiles] = useState(initialFiles);
  const [form, setFormData] = useState(initialFormData);
  const [submitData, setSubmitData] = useState(initialSubmitData);

  useEffect(() => {
    if (!assurers.loading && !assurers.loaded && !assurers.error) {
      setAssurers({ ...assurers, loading: true });
      getAssurerList()
        .then((data: Assurer[]) => setAssurers({
          ...assurers,
          options: data.map(d => ({ value: d._id, label: d.name })),
          loaded: true
        }))
        .catch(e => setAssurers({
          options: [],
          loading: false,
          loaded: false,
          error: true,
          errorMessage: e.message
        }));
    }
  }, [assurers]);

  const handleClose = (submitted = false) => {
    setFiles(initialFiles);
    setFormData(initialFormData);
    setAssurers(initialAssurers);
    setSubmitData(initialSubmitData);
    toggle(submitted);
  }

  const setAssuranceOrganizationId = (value: string) => {
    const portfolioType = form.portfolioType;
    // Reset description state
    const formData = {
      ...initialFormData,
      portfolioType: portfolioType,
      organizationId: value,
      // Reset primary contact info
      assurancePrimaryContactId: undefined,
      assurancePrimaryContactName: undefined,
    };

    setFormData(formData);
  };

  const setPortfolioType = (value: string) =>
    setFormData({
      ...form,
      portfolioType: value,
    });


  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement>) => setFormData({
    ...form,
    [e.target.name]: e.target.value,
  });

  const setPrimaryContact: SetProviderFn = (value, suggestion) => {
    setFormData({
      ...form,
      assurancePrimaryContactId: suggestion ? suggestion._id : undefined,
      assurancePrimaryContactName: suggestion ? suggestion.name : value,
    });
  }

  const handleFileRemove = (fileType: string) => {
    const newFiles = { ...files };
    newFiles[fileType] = undefined;
    setFiles(newFiles);
  };

  const handleFileAdd = (fileType: string, addFiles: FileWithPreview[]) => {
    if (!addFiles) {
      return;
    }

    const singleFile = addFiles[0];
    const newFiles = { ...files };
    getFileData(singleFile).then(newFile => {
      newFiles[fileType] = newFile;
      setFiles(newFiles)
    });
  };

  const getRequest = () => {
    const formData = { ...form, files: files, initiativeId, surveyId };
    if (formData.portfolioId) {
      return G17Client.updateAssurancePortfolio(formData as AssurancePortfolioUpdate, formDataConfig);
    }
    return G17Client.addAssurancePortfolio(formData as AssurancePortfolioCreate, formDataConfig);
  };

  const handleSubmit = async () => {
    setSubmitData({ ...submitData, submitting: true, error: false });
    return getRequest().then(async (data) => {

      const analytics = getAnalytics();
      await analytics.track(AnalyticsEvents.SurveyAssuranceCreated, {
        surveyId: surveyId,
        initiativeId,
      });

      setSubmitData({ ...submitData, submitting: false });
      handleClose(true);
      const url = generateUrl(ROUTES.COMPANY_TRACKER_ASSURANCE,
        {
          initiativeId,
          surveyId,
          assuranceId: data._id
        });
      return history.push(`${url}${history.location.search}`);
    }).catch(e => {
      setSubmitData({
        ...submitData,
        submitting: false,
        error: true,
        errorMessage: e.message
      });
    })
  };

  const formDisabled = requiredFields.some((k) => !form[k]);

  const editDisabled = submitData.submitting;
  const orgId = form['organizationId'];

  return (
    <fieldset disabled={submitData.submitting}>
      <FormGroup>
        <SelectFactory
          selectType={SelectTypes.LabelSelect}
          labelProps={{ for: 'portfolioType', title: 'Assurance Type*' }}
          placeholder='PLEASE SELECT'
          options={optionPortfolioType}
          onChange={(op) => setPortfolioType(op?.value ?? '')}
          isSearchable={false}
          value={optionPortfolioType.find((op) => op.value === form['portfolioType'])}
        />
      </FormGroup>
      <div className={!form['portfolioType'] ? 'disabled' : ''}>
        <FormGroup>
          <SelectFactory
            selectType={SelectTypes.LabelSelect}
            labelProps={{ for: 'Assurance Tracker', title: 'Assurer*' }}
            placeholder='PLEASE SELECT'
            options={assurers.options}
            onChange={(op) => setAssuranceOrganizationId(op?.value ?? '')}
            isSearchable={false}
            value={assurers.options.find((op) => op.value === form['organizationId'])}
          />
        </FormGroup>
      </div>
      <div className={`${!orgId ? 'disabled' : ''}`}>
        <FormGroup>
          <Label>
            Assurer Primary Point of Contact*
          </Label>
          <UserSearch
            key={orgId}
            name={'assurancePrimaryContactName'}
            placeholder={'SEARCH NAME'}
            disabled={!orgId}
            required={true}
            defaultValue={form['assurancePrimaryContactName']}
            organizationId={orgId}
            inputClassName={'form-control'}
            handleValueChange={setPrimaryContact} />
        </FormGroup>

        <FormGroup>
          <Label>
            Assurance Tracker Description
          </Label>
          <Input
            type='textarea'
            disabled={!orgId}
            name={'description'}
            required={true}
            value={form['description']}
            onChange={onChange}
            className='form-control commentBox'
            maxLength={1000}
            rows={3} />
        </FormGroup>

        {RenderAttachment({
          label: 'Supporting Documentation',
          files,
          handleFileAdd,
          handleFileRemove,
          disabled: !orgId || editDisabled,
          metadata: {
            initiativeId,
          },
        })}
      </div>

      <div className='mt-5 d-flex justify-content-end'>
        <Button color='link-secondary' className='mr-3' onClick={() => handleClose(false)}>Cancel</Button>
        <SubmitButton disabled={formDisabled} color='primary' onClick={handleSubmit}>
          {form.portfolioId ? 'Update' : 'Add'}
        </SubmitButton>
      </div>
    </fieldset>
  );
};

export default AssuranceModalCreate;
